// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ConfigurationProjectionLayout from './configuration.projectionLayout';
import * as ConfigurationCore from './configuration.core';
import * as ConfigurationModelLayout from './configuration.modelLayout';
import * as ConfigurationProjectionLayoutGrid from './configuration.projectionLayoutGrid';
import * as ConfigurationProjectionIntegration from './configuration.projectionIntegration';

export enum ValidationStates {
    Valid,
    SoftInvalid,
    HardInvalid
}

export enum AssignmentStates {
    Assignable,
    Conflicting,
    Blocked
}

export enum QuestionTypes {
    String,
    Integer,
    Float,
    Boolean
}

export enum QuestionStates {
    Unassigned,
    SystemAssigned,
    UserAssigned
}

export function isLayoutModel(source: any): source is ILayout {
    // Method Implementation: simple
    return source.modelName === 'Layout';
}

export function isGroupModel(source: any): source is IGroup {
    // Method Implementation: simple
    return source.modelName === 'Group';
}

export function isQuestionModel(source: any): source is IQuestion {
    // Method Implementation: simple
    return source.modelName === 'Question';
}

export function isAnswerModel(source: any): source is IAnswer {
    // Method Implementation: simple
    return source.modelName === 'Answer';
}

export function isKeyedEntityModel(source: any): source is IKeyedEntity {
    // Method Implementation: simple
    return source.modelName === 'KeyedEntity';
}

export function isCachedEntityModel(source: any): source is ICachedEntity {
    // Method Implementation: simple
    return source.modelName === 'CachedEntity';
}

export function isEntityStateModel(source: any): source is IEntityState {
    // Method Implementation: simple
    return source.modelName === 'EntityState';
}

export function isEntityStateQuestionModel(source: any): source is IEntityStateQuestion {
    // Method Implementation: simple
    return source.modelName === 'EntityStateQuestion';
}

export function isEntityContextModel(source: any): source is IEntityContext {
    // Method Implementation: simple
    return source.modelName === 'EntityContext';
}

export function isGridContextModel(source: any): source is IGridContext {
    // Method Implementation: simple
    return source.modelName === 'GridContext';
}

export function isStateContextModel(source: any): source is IStateContext {
    // Method Implementation: simple
    return source.modelName === 'StateContext';
}

export interface LayoutZodSchemaShape {
    groups?: GroupZodSchemaShape[];
}

export interface GroupZodSchemaShape {
    name?: string;

    label?: string;

    questions?: QuestionZodSchemaShape[];
}

export interface QuestionZodSchemaShape {
    name?: string;

    label?: string;

    uom?: string;

    hint?: string;

    value?: any;

    answers?: AnswerZodSchemaShape[];

    step?: number;

    multiple?: boolean;

    hidden?: boolean;

    required?: boolean;

    disabled?: boolean;

    type?: 'String' | 'Integer' | 'Float' | 'Boolean';

    min?: number;

    max?: number;

    precision?: number;
}

export interface AnswerZodSchemaShape {
    label?: string;

    value?: any;

    hidden?: boolean;

    disabled?: boolean;

    assignmentState?: 'Assignable' | 'Conflicting' | 'Blocked';
}

export interface KeyedEntityZodSchemaShape {
    entityId?: string;

    groups?: ConfigurationProjectionLayout.GroupZodSchemaShape[];

    validationMessages?: ConfigurationProjectionLayout.ValidationMessageZodSchemaShape[];

    conflict?: ConfigurationProjectionLayout.ConflictScenarioZodSchemaShape;

    state?: 'Valid' | 'Invalid' | 'Incomplete';

    historicalStates?: ConfigurationProjectionLayout.HistoricalStateZodSchemaShape[];

    layouts?: ConfigurationProjectionLayout.LinkedLayoutZodSchemaShape[];

    key?: string;
}

export interface CachedEntityZodSchemaShape {
    coreEntity?: ConfigurationCore.EntityZodSchemaShape;

    layout?: ConfigurationModelLayout.LayoutZodSchemaShape;
}

export interface EntityStateZodSchemaShape {
    entityName?: string;

    questions?: EntityStateQuestionZodSchemaShape[];
}

export interface EntityStateQuestionZodSchemaShape {
    name?: string;

    value?: string;

    restrict?: boolean;
}

export interface EntityContextZodSchemaShape {
    entityKey?: string;

    layoutName?: string;
}

export interface GridContextZodSchemaShape {
    entityKey?: string;

    gridName?: string;
}

export interface StateContextZodSchemaShape {
    entityKey?: string;
}

// Artifact Implementation: artifacts/zodSchemaType
export const LayoutZodSchema: Zod.ZodSchema<LayoutZodSchemaShape> = Zod.lazy(() => Zod.object({
    groups: Zod.array(GroupZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const GroupZodSchema: Zod.ZodSchema<GroupZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    label: Zod.string().optional(),
    questions: Zod.array(QuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const QuestionZodSchema: Zod.ZodSchema<QuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    label: Zod.string().optional(),
    uom: Zod.string().optional(),
    hint: Zod.string().optional(),
    value: Zod.any().optional(),
    answers: Zod.array(AnswerZodSchema).optional(),
    step: Zod.number().optional(),
    multiple: Zod.boolean().optional(),
    hidden: Zod.boolean().optional(),
    required: Zod.boolean().optional(),
    disabled: Zod.boolean().optional(),
    type: Zod.enum([ 'String', 'Integer', 'Float', 'Boolean' ]).optional(),
    min: Zod.number().optional(),
    max: Zod.number().optional(),
    precision: Zod.number().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const AnswerZodSchema: Zod.ZodSchema<AnswerZodSchemaShape> = Zod.lazy(() => Zod.object({
    label: Zod.string().optional(),
    value: Zod.any().optional(),
    hidden: Zod.boolean().optional(),
    disabled: Zod.boolean().optional(),
    assignmentState: Zod.enum([ 'Assignable', 'Conflicting', 'Blocked' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const KeyedEntityZodSchema: Zod.ZodSchema<KeyedEntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityId: Zod.string().optional(),
    groups: Zod.array(ConfigurationProjectionLayout.GroupZodSchema).optional(),
    validationMessages: Zod.array(ConfigurationProjectionLayout.ValidationMessageZodSchema).optional(),
    conflict: ConfigurationProjectionLayout.ConflictScenarioZodSchema.optional(),
    state: Zod.enum([ 'Valid', 'Invalid', 'Incomplete' ]).optional(),
    historicalStates: Zod.array(ConfigurationProjectionLayout.HistoricalStateZodSchema).optional(),
    layouts: Zod.array(ConfigurationProjectionLayout.LinkedLayoutZodSchema).optional(),
    key: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const CachedEntityZodSchema: Zod.ZodSchema<CachedEntityZodSchemaShape> = Zod.lazy(() => Zod.object({
    coreEntity: ConfigurationCore.EntityZodSchema.optional(),
    layout: ConfigurationModelLayout.LayoutZodSchema.optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityStateZodSchema: Zod.ZodSchema<EntityStateZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityName: Zod.string().optional(),
    questions: Zod.array(EntityStateQuestionZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityStateQuestionZodSchema: Zod.ZodSchema<EntityStateQuestionZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    value: Zod.string().optional(),
    restrict: Zod.boolean().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const EntityContextZodSchema: Zod.ZodSchema<EntityContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional(),
    layoutName: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const GridContextZodSchema: Zod.ZodSchema<GridContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional(),
    gridName: Zod.string().optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const StateContextZodSchema: Zod.ZodSchema<StateContextZodSchemaShape> = Zod.lazy(() => Zod.object({
    entityKey: Zod.string().optional()
}).nonstrict());

export interface ILayout extends Wellspring.IModel {
    readonly modelName: string;

    readonly groups: Group[];

    readonly isGroupsDefined: boolean;
}

export interface IGroup extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly label: string;

    readonly questions: Question[];

    readonly isNameDefined: boolean;

    readonly isLabelDefined: boolean;

    readonly isQuestionsDefined: boolean;
}

export interface IQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly label: string;

    readonly uom: string;

    readonly hint: string;

    readonly value: any;

    readonly answers: Answer[];

    readonly step: number;

    readonly multiple: boolean;

    readonly hidden: boolean;

    readonly required: boolean;

    readonly disabled: boolean;

    readonly type: QuestionTypes;

    readonly min: number;

    readonly max: number;

    readonly precision: number;

    readonly isNameDefined: boolean;

    readonly isLabelDefined: boolean;

    readonly isUomDefined: boolean;

    readonly isHintDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isAnswersDefined: boolean;

    readonly isStepDefined: boolean;

    readonly isMultipleDefined: boolean;

    readonly isHiddenDefined: boolean;

    readonly isRequiredDefined: boolean;

    readonly isDisabledDefined: boolean;

    readonly isTypeDefined: boolean;

    readonly isMinDefined: boolean;

    readonly isMaxDefined: boolean;

    readonly isPrecisionDefined: boolean;
}

export interface IAnswer extends Wellspring.IModel {
    readonly modelName: string;

    readonly label: string;

    readonly value: any;

    readonly hidden: boolean;

    readonly disabled: boolean;

    readonly assignmentState: AssignmentStates;

    readonly isLabelDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isHiddenDefined: boolean;

    readonly isDisabledDefined: boolean;

    readonly isAssignmentStateDefined: boolean;
}

export interface IKeyedEntity extends ConfigurationProjectionLayout.IEntity, Wellspring.IModel {
    readonly modelName: string;

    readonly key: string;

    readonly isKeyDefined: boolean;
}

export interface ICachedEntity extends Wellspring.IModel {
    readonly modelName: string;

    readonly coreEntity: ConfigurationCore.Entity;

    readonly layout: ConfigurationModelLayout.Layout;

    readonly isCoreEntityDefined: boolean;

    readonly isLayoutDefined: boolean;
}

export interface IEntityState extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityName: string;

    readonly questions: EntityStateQuestion[];

    readonly isEntityNameDefined: boolean;

    readonly isQuestionsDefined: boolean;
}

export interface IEntityStateQuestion extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly value: string;

    readonly restrict?: boolean;

    readonly isNameDefined: boolean;

    readonly isValueDefined: boolean;

    readonly isRestrictDefined: boolean;
}

export interface IEntityContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly layoutName: string;

    readonly isEntityKeyDefined: boolean;

    readonly isLayoutNameDefined: boolean;
}

export interface IGridContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly gridName: string;

    readonly isEntityKeyDefined: boolean;

    readonly isGridNameDefined: boolean;
}

export interface IStateContext extends Wellspring.IModel {
    readonly modelName: string;

    readonly entityKey: string;

    readonly isEntityKeyDefined: boolean;
}

export class Layout implements ILayout {
    public static readonly modelName: string = 'Layout';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'groups' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Layout.modelName;
    }

    private _groups: Group[];

    public get groups(): Group[] {
        // Method Implementation: simple
        return this._groups;
    }

    public set groups(value: Group[]) {
        // Method Implementation: simple
        this._groups = value;
        this._isGroupsDefined = true;
    }

    private _isGroupsDefined: boolean;

    public get isGroupsDefined(): boolean {
        // Method Implementation: simple
        return this._isGroupsDefined;
    }

    public constructor();
    public constructor(source: ILayout);
    public constructor(source: Partial<Pick<Layout, 'groups'>>);
    public constructor(source?: ILayout | Partial<Pick<Layout, 'groups'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isLayoutModel(source)) {
                if (source.isGroupsDefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
            }
            else {
                if (source.groups !== undefined) {
                    this._groups = source.groups;
                    this._isGroupsDefined = true;
                }
                else {
                    this._groups = [];
                    this._isGroupsDefined = true;
                }
            }
        }
        else {
            this._groups = [];
            this._isGroupsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Layout.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isGroupsDefined && this.groups !== undefined)
            definedProperties.push('groups');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'groups':
                return this.groups;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isGroupsDefined && this.groups !== undefined) {
            if (this.groups !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.groups.length));
                for (let i = 0; i < this.groups.length; i++) {
                    const item = this.groups[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('groups', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'groups':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.groups = [];
                
                    let item: Group = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Group.modelName) {
                                item = new Group();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Group.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'groups' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'groups'`);
                        }
                
                        this.groups.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'groups' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'groups'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // groups
        if (this.isGroupsDefined && this.groups !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.groups === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.groups.length);
            
                for (const item of this.groups) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // groups
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Group;
            
                    this.groups = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Group();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.groups.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Layout {
        // Method Implementation: simple
        const schema = LayoutZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: LayoutZodSchemaShape): Layout {
        // Method Implementation: parseZodSchema
        return new Layout({
            groups: schema.groups?.map(i => Group.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            groups: this.isGroupsDefined ? (this.groups?.map(item => item)) : undefined,
        };
    }

    public clone(): Layout {
        // Method Implementation: clone
        return new Layout({
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined
        });
    }
}

export class Group implements IGroup {
    public static readonly modelName: string = 'Group';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'label', 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Group.modelName;
    }

    private _name: string;

    private _label: string;

    private _questions: Question[];

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get label(): string {
        // Method Implementation: simple
        return this._label;
    }

    public get questions(): Question[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set label(value: string) {
        // Method Implementation: simple
        this._label = value;
        this._isLabelDefined = true;
    }

    public set questions(value: Question[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isNameDefined: boolean;

    private _isLabelDefined: boolean;

    private _isQuestionsDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isLabelDefined(): boolean {
        // Method Implementation: simple
        return this._isLabelDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IGroup);
    public constructor(source: Partial<Pick<Group, 'name' | 'label' | 'questions'>>);
    public constructor(source?: IGroup | Partial<Pick<Group, 'name' | 'label' | 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGroupModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isLabelDefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.label !== undefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._label = '';
            this._isLabelDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Group.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isLabelDefined && this.label !== undefined)
            definedProperties.push('label');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'label':
                return this.label;
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isLabelDefined && this.label !== undefined) {
            if (this.label !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.label);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'label':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.label = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'label' cannot be null`);
                    this.label = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'label'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: Question = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Question.modelName) {
                                item = new Question();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Question.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // label
        if (this.isLabelDefined && this.label !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.label === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.label);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // label
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.label = <any>null;
                    break;
                case 1:
                    this.label = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Question;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Question();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Group {
        // Method Implementation: simple
        const schema = GroupZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GroupZodSchemaShape): Group {
        // Method Implementation: parseZodSchema
        return new Group({
            name: schema.name,
            label: schema.label,
            questions: schema.questions?.map(i => Question.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            label: this.isLabelDefined ? ((this.label === null ? undefined : this.label)) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): Group {
        // Method Implementation: clone
        return new Group({
            name: this.isNameDefined ? this.name : undefined,
            label: this.isLabelDefined ? this.label : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export class Question implements IQuestion {
    public static readonly modelName: string = 'Question';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'label', 'uom', 'hint', 'value', 'answers', 'step', 'multiple', 'hidden', 'required', 'disabled', 'type', 'min', 'max', 'precision' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Question.modelName;
    }

    private _name: string;

    private _label: string;

    private _uom: string;

    private _hint: string;

    private _value: any;

    private _answers: Answer[];

    private _step: number;

    private _multiple: boolean;

    private _hidden: boolean;

    private _required: boolean;

    private _disabled: boolean;

    private _type: QuestionTypes;

    private _min: number;

    private _max: number;

    private _precision: number;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get label(): string {
        // Method Implementation: simple
        return this._label;
    }

    public get uom(): string {
        // Method Implementation: simple
        return this._uom;
    }

    public get hint(): string {
        // Method Implementation: simple
        return this._hint;
    }

    public get value(): any {
        // Method Implementation: simple
        return this._value;
    }

    public get answers(): Answer[] {
        // Method Implementation: simple
        return this._answers;
    }

    public get step(): number {
        // Method Implementation: simple
        return this._step;
    }

    public get multiple(): boolean {
        // Method Implementation: simple
        return this._multiple;
    }

    public get hidden(): boolean {
        // Method Implementation: simple
        return this._hidden;
    }

    public get required(): boolean {
        // Method Implementation: simple
        return this._required;
    }

    public get disabled(): boolean {
        // Method Implementation: simple
        return this._disabled;
    }

    public get type(): QuestionTypes {
        // Method Implementation: simple
        return this._type;
    }

    public get min(): number {
        // Method Implementation: simple
        return this._min;
    }

    public get max(): number {
        // Method Implementation: simple
        return this._max;
    }

    public get precision(): number {
        // Method Implementation: simple
        return this._precision;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set label(value: string) {
        // Method Implementation: simple
        this._label = value;
        this._isLabelDefined = true;
    }

    public set uom(value: string) {
        // Method Implementation: simple
        this._uom = value;
        this._isUomDefined = true;
    }

    public set hint(value: string) {
        // Method Implementation: simple
        this._hint = value;
        this._isHintDefined = true;
    }

    public set value(value: any) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set answers(value: Answer[]) {
        // Method Implementation: simple
        this._answers = value;
        this._isAnswersDefined = true;
    }

    public set step(value: number) {
        // Method Implementation: simple
        this._step = value;
        this._isStepDefined = true;
    }

    public set multiple(value: boolean) {
        // Method Implementation: simple
        this._multiple = value;
        this._isMultipleDefined = true;
    }

    public set hidden(value: boolean) {
        // Method Implementation: simple
        this._hidden = value;
        this._isHiddenDefined = true;
    }

    public set required(value: boolean) {
        // Method Implementation: simple
        this._required = value;
        this._isRequiredDefined = true;
    }

    public set disabled(value: boolean) {
        // Method Implementation: simple
        this._disabled = value;
        this._isDisabledDefined = true;
    }

    public set type(value: QuestionTypes) {
        // Method Implementation: simple
        this._type = value;
        this._isTypeDefined = true;
    }

    public set min(value: number) {
        // Method Implementation: simple
        this._min = value;
        this._isMinDefined = true;
    }

    public set max(value: number) {
        // Method Implementation: simple
        this._max = value;
        this._isMaxDefined = true;
    }

    public set precision(value: number) {
        // Method Implementation: simple
        this._precision = value;
        this._isPrecisionDefined = true;
    }

    private _isNameDefined: boolean;

    private _isLabelDefined: boolean;

    private _isUomDefined: boolean;

    private _isHintDefined: boolean;

    private _isValueDefined: boolean;

    private _isAnswersDefined: boolean;

    private _isStepDefined: boolean;

    private _isMultipleDefined: boolean;

    private _isHiddenDefined: boolean;

    private _isRequiredDefined: boolean;

    private _isDisabledDefined: boolean;

    private _isTypeDefined: boolean;

    private _isMinDefined: boolean;

    private _isMaxDefined: boolean;

    private _isPrecisionDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isLabelDefined(): boolean {
        // Method Implementation: simple
        return this._isLabelDefined;
    }

    public get isUomDefined(): boolean {
        // Method Implementation: simple
        return this._isUomDefined;
    }

    public get isHintDefined(): boolean {
        // Method Implementation: simple
        return this._isHintDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isAnswersDefined(): boolean {
        // Method Implementation: simple
        return this._isAnswersDefined;
    }

    public get isStepDefined(): boolean {
        // Method Implementation: simple
        return this._isStepDefined;
    }

    public get isMultipleDefined(): boolean {
        // Method Implementation: simple
        return this._isMultipleDefined;
    }

    public get isHiddenDefined(): boolean {
        // Method Implementation: simple
        return this._isHiddenDefined;
    }

    public get isRequiredDefined(): boolean {
        // Method Implementation: simple
        return this._isRequiredDefined;
    }

    public get isDisabledDefined(): boolean {
        // Method Implementation: simple
        return this._isDisabledDefined;
    }

    public get isTypeDefined(): boolean {
        // Method Implementation: simple
        return this._isTypeDefined;
    }

    public get isMinDefined(): boolean {
        // Method Implementation: simple
        return this._isMinDefined;
    }

    public get isMaxDefined(): boolean {
        // Method Implementation: simple
        return this._isMaxDefined;
    }

    public get isPrecisionDefined(): boolean {
        // Method Implementation: simple
        return this._isPrecisionDefined;
    }

    public constructor();
    public constructor(source: IQuestion);
    public constructor(source: Partial<Pick<Question, 'name' | 'label' | 'uom' | 'hint' | 'value' | 'answers' | 'step' | 'multiple' | 'hidden' | 'required' | 'disabled' | 'type' | 'min' | 'max' | 'precision'>>);
    public constructor(source?: IQuestion | Partial<Pick<Question, 'name' | 'label' | 'uom' | 'hint' | 'value' | 'answers' | 'step' | 'multiple' | 'hidden' | 'required' | 'disabled' | 'type' | 'min' | 'max' | 'precision'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isLabelDefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.isUomDefined) {
                    this._uom = source.uom;
                    this._isUomDefined = true;
                }
                else {
                    this._uom = '';
                    this._isUomDefined = false;
                }
                
                if (source.isHintDefined) {
                    this._hint = source.hint;
                    this._isHintDefined = true;
                }
                else {
                    this._hint = '';
                    this._isHintDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = <any>undefined;
                    this._isValueDefined = false;
                }
                
                if (source.isAnswersDefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.isStepDefined) {
                    this._step = source.step;
                    this._isStepDefined = true;
                }
                else {
                    this._step = 0;
                    this._isStepDefined = false;
                }
                
                if (source.isMultipleDefined) {
                    this._multiple = source.multiple;
                    this._isMultipleDefined = true;
                }
                else {
                    this._multiple = false;
                    this._isMultipleDefined = false;
                }
                
                if (source.isHiddenDefined) {
                    this._hidden = source.hidden;
                    this._isHiddenDefined = true;
                }
                else {
                    this._hidden = false;
                    this._isHiddenDefined = false;
                }
                
                if (source.isRequiredDefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.isDisabledDefined) {
                    this._disabled = source.disabled;
                    this._isDisabledDefined = true;
                }
                else {
                    this._disabled = false;
                    this._isDisabledDefined = false;
                }
                
                if (source.isTypeDefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = QuestionTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.isMinDefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.isMaxDefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
                
                if (source.isPrecisionDefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.label !== undefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.uom !== undefined) {
                    this._uom = source.uom;
                    this._isUomDefined = true;
                }
                else {
                    this._uom = '';
                    this._isUomDefined = false;
                }
                
                if (source.hint !== undefined) {
                    this._hint = source.hint;
                    this._isHintDefined = true;
                }
                else {
                    this._hint = '';
                    this._isHintDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = <any>undefined;
                    this._isValueDefined = false;
                }
                
                if (source.answers !== undefined) {
                    this._answers = source.answers;
                    this._isAnswersDefined = true;
                }
                else {
                    this._answers = [];
                    this._isAnswersDefined = true;
                }
                
                if (source.step !== undefined) {
                    this._step = source.step;
                    this._isStepDefined = true;
                }
                else {
                    this._step = 0;
                    this._isStepDefined = false;
                }
                
                if (source.multiple !== undefined) {
                    this._multiple = source.multiple;
                    this._isMultipleDefined = true;
                }
                else {
                    this._multiple = false;
                    this._isMultipleDefined = false;
                }
                
                if (source.hidden !== undefined) {
                    this._hidden = source.hidden;
                    this._isHiddenDefined = true;
                }
                else {
                    this._hidden = false;
                    this._isHiddenDefined = false;
                }
                
                if (source.required !== undefined) {
                    this._required = source.required;
                    this._isRequiredDefined = true;
                }
                else {
                    this._required = false;
                    this._isRequiredDefined = false;
                }
                
                if (source.disabled !== undefined) {
                    this._disabled = source.disabled;
                    this._isDisabledDefined = true;
                }
                else {
                    this._disabled = false;
                    this._isDisabledDefined = false;
                }
                
                if (source.type !== undefined) {
                    this._type = source.type;
                    this._isTypeDefined = true;
                }
                else {
                    this._type = QuestionTypes.String;
                    this._isTypeDefined = false;
                }
                
                if (source.min !== undefined) {
                    this._min = source.min;
                    this._isMinDefined = true;
                }
                else {
                    this._min = 0;
                    this._isMinDefined = false;
                }
                
                if (source.max !== undefined) {
                    this._max = source.max;
                    this._isMaxDefined = true;
                }
                else {
                    this._max = 0;
                    this._isMaxDefined = false;
                }
                
                if (source.precision !== undefined) {
                    this._precision = source.precision;
                    this._isPrecisionDefined = true;
                }
                else {
                    this._precision = 0;
                    this._isPrecisionDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._label = '';
            this._isLabelDefined = false;
        
            this._uom = '';
            this._isUomDefined = false;
        
            this._hint = '';
            this._isHintDefined = false;
        
            this._value = <any>undefined;
            this._isValueDefined = false;
        
            this._answers = [];
            this._isAnswersDefined = true;
        
            this._step = 0;
            this._isStepDefined = false;
        
            this._multiple = false;
            this._isMultipleDefined = false;
        
            this._hidden = false;
            this._isHiddenDefined = false;
        
            this._required = false;
            this._isRequiredDefined = false;
        
            this._disabled = false;
            this._isDisabledDefined = false;
        
            this._type = QuestionTypes.String;
            this._isTypeDefined = false;
        
            this._min = 0;
            this._isMinDefined = false;
        
            this._max = 0;
            this._isMaxDefined = false;
        
            this._precision = 0;
            this._isPrecisionDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Question.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isLabelDefined && this.label !== undefined)
            definedProperties.push('label');
        if (this.isUomDefined && this.uom !== undefined)
            definedProperties.push('uom');
        if (this.isHintDefined && this.hint !== undefined)
            definedProperties.push('hint');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isAnswersDefined && this.answers !== undefined)
            definedProperties.push('answers');
        if (this.isStepDefined && this.step !== undefined)
            definedProperties.push('step');
        if (this.isMultipleDefined && this.multiple !== undefined)
            definedProperties.push('multiple');
        if (this.isHiddenDefined && this.hidden !== undefined)
            definedProperties.push('hidden');
        if (this.isRequiredDefined && this.required !== undefined)
            definedProperties.push('required');
        if (this.isDisabledDefined && this.disabled !== undefined)
            definedProperties.push('disabled');
        if (this.isTypeDefined && this.type !== undefined)
            definedProperties.push('type');
        if (this.isMinDefined && this.min !== undefined)
            definedProperties.push('min');
        if (this.isMaxDefined && this.max !== undefined)
            definedProperties.push('max');
        if (this.isPrecisionDefined && this.precision !== undefined)
            definedProperties.push('precision');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'label':
                return this.label;
            case 'uom':
                return this.uom;
            case 'hint':
                return this.hint;
            case 'value':
                return this.value;
            case 'answers':
                return this.answers;
            case 'step':
                return this.step;
            case 'multiple':
                return this.multiple;
            case 'hidden':
                return this.hidden;
            case 'required':
                return this.required;
            case 'disabled':
                return this.disabled;
            case 'type':
                return this.type;
            case 'min':
                return this.min;
            case 'max':
                return this.max;
            case 'precision':
                return this.precision;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isLabelDefined && this.label !== undefined) {
            if (this.label !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.label);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'null'));
            }
        }
        
        if (this.isUomDefined && this.uom !== undefined) {
            if (this.uom !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('uom', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.uom);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('uom', 'null'));
            }
        }
        
        if (this.isHintDefined && this.hint !== undefined) {
            if (this.hint !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hint', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.hint);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hint', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'any'));
        
                // Implementation: axon/write/any
                if (_.isString(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('string'));
                    writer.writeStringValue(this.value);    
                }
                else if (_.isInteger(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('int32'));
                    writer.writeIntegerValue(this.value);    
                }
                else if (_.isNumber(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('double'));
                    writer.writeDoubleValue(this.value);    
                }
                else {
                    throw new Error('Cannot serialize unknown indefinite type');
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isAnswersDefined && this.answers !== undefined) {
            if (this.answers !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.answers.length));
                for (let i = 0; i < this.answers.length; i++) {
                    const item = this.answers[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('answers', 'null'));
            }
        }
        
        if (this.isStepDefined && this.step !== undefined) {
            if (this.step !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('step', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.step);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('step', 'null'));
            }
        }
        
        if (this.isMultipleDefined && this.multiple !== undefined) {
            if (this.multiple !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multiple', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.multiple);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('multiple', 'null'));
            }
        }
        
        if (this.isHiddenDefined && this.hidden !== undefined) {
            if (this.hidden !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hidden', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.hidden);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hidden', 'null'));
            }
        }
        
        if (this.isRequiredDefined && this.required !== undefined) {
            if (this.required !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.required);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('required', 'null'));
            }
        }
        
        if (this.isDisabledDefined && this.disabled !== undefined) {
            if (this.disabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('disabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.disabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('disabled', 'null'));
            }
        }
        
        if (this.isTypeDefined && this.type !== undefined) {
            if (this.type !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.type);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('type', 'null'));
            }
        }
        
        if (this.isMinDefined && this.min !== undefined) {
            if (this.min !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.min);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('min', 'null'));
            }
        }
        
        if (this.isMaxDefined && this.max !== undefined) {
            if (this.max !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.max);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('max', 'null'));
            }
        }
        
        if (this.isPrecisionDefined && this.precision !== undefined) {
            if (this.precision !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'int32'));
        
                // Implementation: axon/write/int32
                writer.writeIntegerValue(this.precision);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('precision', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'label':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.label = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'label' cannot be null`);
                    this.label = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'label'`);
                }
        
                return true;
            case 'uom':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.uom = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'uom' cannot be null`);
                    this.uom = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'uom'`);
                }
        
                return true;
            case 'hint':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.hint = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'hint' cannot be null`);
                    this.hint = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'hint'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/any
                if (propertyHeader.type === 'any') {
                    const indefiniteValueHeader = reader.readIndefiniteValueHeader();
                
                    switch (indefiniteValueHeader.valueType) {
                        case 'string':
                            this.value = reader.readStringValue();
                            break;
                        case 'int32':
                            this.value = reader.readIntegerValue();
                            break;
                        case 'double':
                            this.value = reader.readDoubleValue();
                            break;
                        default:
                            throw new Error('Cannot serialize unknown indefinite type');
                    }
                }
                else if (propertyHeader.type === 'null') {
                    this.value = null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'answers':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.answers = [];
                
                    let item: Answer = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Answer.modelName) {
                                item = new Answer();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Answer.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'answers' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'answers'`);
                        }
                
                        this.answers.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'answers' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'answers'`);
                }
        
                return true;
            case 'step':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.step = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'step' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'step'`);
                }
        
                return true;
            case 'multiple':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.multiple = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'multiple' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'multiple'`);
                }
        
                return true;
            case 'hidden':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.hidden = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'hidden' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'hidden'`);
                }
        
                return true;
            case 'required':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.required = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'required' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'required'`);
                }
        
                return true;
            case 'disabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.disabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'disabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'disabled'`);
                }
        
                return true;
            case 'type':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.type = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'type' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'type'`);
                }
        
                return true;
            case 'min':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.min = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.min = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'min' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'min'`);
                }
        
                return true;
            case 'max':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.max = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.max = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'max' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'max'`);
                }
        
                return true;
            case 'precision':
                // Implementation: axon/readProperty/int32
                if (propertyHeader.type === 'int32') {
                    this.precision = reader.readIntegerValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'precision' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'precision'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // label
        if (this.isLabelDefined && this.label !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.label === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.label);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // uom
        if (this.isUomDefined && this.uom !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.uom === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.uom);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // hint
        if (this.isHintDefined && this.hint !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.hint === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.hint);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/any
            if (this.value === null) {
                writer.writeByteValue(0);
            }
            else if (_.isBoolean(this.value)) {
                writer.writeByteValue(1);
                writer.writeBooleanValue(this.value);
            }
            else if (_.isString(this.value)) {
                writer.writeByteValue(2);
                writer.writeStringValue(this.value);
            }
            else if (_.isInteger(this.value)) {
                writer.writeByteValue(3);
                writer.writeIntegerValue(this.value);
            }
            else if (_.isNumber(this.value)) {
                writer.writeByteValue(5);
                writer.writeDoubleValue(this.value);
            }
            else {
                throw new Error('Unsupported indefinite type');
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // answers
        if (this.isAnswersDefined && this.answers !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.answers === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.answers.length);
            
                for (const item of this.answers) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // step
        if (this.isStepDefined && this.step !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.step);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // multiple
        if (this.isMultipleDefined && this.multiple !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.multiple);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // hidden
        if (this.isHiddenDefined && this.hidden !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.hidden);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // required
        if (this.isRequiredDefined && this.required !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.required);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // disabled
        if (this.isDisabledDefined && this.disabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.disabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // type
        if (this.isTypeDefined && this.type !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.type);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // min
        if (this.isMinDefined && this.min !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.min);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // max
        if (this.isMaxDefined && this.max !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.max);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // precision
        if (this.isPrecisionDefined && this.precision !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/int32
            writer.writeIntegerValue(this.precision);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // label
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.label = <any>null;
                    break;
                case 1:
                    this.label = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // uom
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.uom = <any>null;
                    break;
                case 1:
                    this.uom = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // hint
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.hint = <any>null;
                    break;
                case 1:
                    this.hint = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: any
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = null;
                    break;
                case 1:
                    this.value = reader.readBooleanValue();
                    break;
                case 2:
                    this.value = reader.readStringValue();
                    break;
                case 3:
                    this.value = reader.readIntegerValue();
                    break;
                case 4:
                    this.value = reader.readFloatValue();
                    break;
                case 5:
                    this.value = reader.readDoubleValue();
                    break;
                default:
                    throw new Error('Invalid indefinite type code');
            }
        }
        
        // answers
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Answer;
            
                    this.answers = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Answer();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.answers.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // step
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.step = reader.readIntegerValue();
        }
        
        // multiple
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.multiple = reader.readBooleanValue();
        }
        
        // hidden
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.hidden = reader.readBooleanValue();
        }
        
        // required
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.required = reader.readBooleanValue();
        }
        
        // disabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.disabled = reader.readBooleanValue();
        }
        
        // type
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.type = reader.readEnumValue();
        }
        
        // min
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.min = reader.readDoubleValue();
        }
        
        // max
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.max = reader.readDoubleValue();
        }
        
        // precision
        if (reader.readBooleanValue())
        {
            // Implementation: int32
            this.precision = reader.readIntegerValue();
        }
    }

    public static parse(hash: unknown): Question {
        // Method Implementation: simple
        const schema = QuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: QuestionZodSchemaShape): Question {
        // Method Implementation: parseZodSchema
        return new Question({
            name: schema.name,
            label: schema.label,
            uom: schema.uom,
            hint: schema.hint,
            value: schema.value,
            answers: schema.answers?.map(i => Answer.parseZodSchema(i)),
            step: schema.step,
            multiple: schema.multiple,
            hidden: schema.hidden,
            required: schema.required,
            disabled: schema.disabled,
            type: (() => {
            if (!schema.type)
                return;
        
            switch (schema.type) {
                case 'String':
                    return QuestionTypes.String;
                case 'Integer':
                    return QuestionTypes.Integer;
                case 'Float':
                    return QuestionTypes.Float;
                case 'Boolean':
                    return QuestionTypes.Boolean;
                default:
                    throw new Error(`Enum type "schema.type" not recognized`);
            }
        })(),
            min: schema.min,
            max: schema.max,
            precision: schema.precision
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            label: this.isLabelDefined ? ((this.label === null ? undefined : this.label)) : undefined,
            uom: this.isUomDefined ? ((this.uom === null ? undefined : this.uom)) : undefined,
            hint: this.isHintDefined ? ((this.hint === null ? undefined : this.hint)) : undefined,
            value: this.isValueDefined ? (this.value) : undefined,
            answers: this.isAnswersDefined ? (this.answers?.map(item => item)) : undefined,
            step: this.isStepDefined ? (this.step) : undefined,
            multiple: this.isMultipleDefined ? (this.multiple) : undefined,
            hidden: this.isHiddenDefined ? (this.hidden) : undefined,
            required: this.isRequiredDefined ? (this.required) : undefined,
            disabled: this.isDisabledDefined ? (this.disabled) : undefined,
            type: this.isTypeDefined ? (QuestionTypes[this.type]) : undefined,
            min: this.isMinDefined ? (this.min) : undefined,
            max: this.isMaxDefined ? (this.max) : undefined,
            precision: this.isPrecisionDefined ? (this.precision) : undefined,
        };
    }

    public clone(): Question {
        // Method Implementation: clone
        return new Question({
            name: this.isNameDefined ? this.name : undefined,
            label: this.isLabelDefined ? this.label : undefined,
            uom: this.isUomDefined ? this.uom : undefined,
            hint: this.isHintDefined ? this.hint : undefined,
            value: this.isValueDefined ? this.value : undefined,
            answers: this.isAnswersDefined ? this.answers?.map(i => i.clone()) : undefined,
            step: this.isStepDefined ? this.step : undefined,
            multiple: this.isMultipleDefined ? this.multiple : undefined,
            hidden: this.isHiddenDefined ? this.hidden : undefined,
            required: this.isRequiredDefined ? this.required : undefined,
            disabled: this.isDisabledDefined ? this.disabled : undefined,
            type: this.isTypeDefined ? this.type : undefined,
            min: this.isMinDefined ? this.min : undefined,
            max: this.isMaxDefined ? this.max : undefined,
            precision: this.isPrecisionDefined ? this.precision : undefined
        });
    }
}

export class Answer implements IAnswer {
    public static readonly modelName: string = 'Answer';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'label', 'value', 'hidden', 'disabled', 'assignmentState' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Answer.modelName;
    }

    private _label: string;

    private _value: any;

    private _hidden: boolean;

    private _disabled: boolean;

    private _assignmentState: AssignmentStates;

    public get label(): string {
        // Method Implementation: simple
        return this._label;
    }

    public get value(): any {
        // Method Implementation: simple
        return this._value;
    }

    public get hidden(): boolean {
        // Method Implementation: simple
        return this._hidden;
    }

    public get disabled(): boolean {
        // Method Implementation: simple
        return this._disabled;
    }

    public get assignmentState(): AssignmentStates {
        // Method Implementation: simple
        return this._assignmentState;
    }

    public set label(value: string) {
        // Method Implementation: simple
        this._label = value;
        this._isLabelDefined = true;
    }

    public set value(value: any) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set hidden(value: boolean) {
        // Method Implementation: simple
        this._hidden = value;
        this._isHiddenDefined = true;
    }

    public set disabled(value: boolean) {
        // Method Implementation: simple
        this._disabled = value;
        this._isDisabledDefined = true;
    }

    public set assignmentState(value: AssignmentStates) {
        // Method Implementation: simple
        this._assignmentState = value;
        this._isAssignmentStateDefined = true;
    }

    private _isLabelDefined: boolean;

    private _isValueDefined: boolean;

    private _isHiddenDefined: boolean;

    private _isDisabledDefined: boolean;

    private _isAssignmentStateDefined: boolean;

    public get isLabelDefined(): boolean {
        // Method Implementation: simple
        return this._isLabelDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isHiddenDefined(): boolean {
        // Method Implementation: simple
        return this._isHiddenDefined;
    }

    public get isDisabledDefined(): boolean {
        // Method Implementation: simple
        return this._isDisabledDefined;
    }

    public get isAssignmentStateDefined(): boolean {
        // Method Implementation: simple
        return this._isAssignmentStateDefined;
    }

    public constructor();
    public constructor(source: IAnswer);
    public constructor(source: Partial<Pick<Answer, 'label' | 'value' | 'hidden' | 'disabled' | 'assignmentState'>>);
    public constructor(source?: IAnswer | Partial<Pick<Answer, 'label' | 'value' | 'hidden' | 'disabled' | 'assignmentState'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isAnswerModel(source)) {
                if (source.isLabelDefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = <any>undefined;
                    this._isValueDefined = false;
                }
                
                if (source.isHiddenDefined) {
                    this._hidden = source.hidden;
                    this._isHiddenDefined = true;
                }
                else {
                    this._hidden = false;
                    this._isHiddenDefined = false;
                }
                
                if (source.isDisabledDefined) {
                    this._disabled = source.disabled;
                    this._isDisabledDefined = true;
                }
                else {
                    this._disabled = false;
                    this._isDisabledDefined = false;
                }
                
                if (source.isAssignmentStateDefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = AssignmentStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
            else {
                if (source.label !== undefined) {
                    this._label = source.label;
                    this._isLabelDefined = true;
                }
                else {
                    this._label = '';
                    this._isLabelDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = <any>undefined;
                    this._isValueDefined = false;
                }
                
                if (source.hidden !== undefined) {
                    this._hidden = source.hidden;
                    this._isHiddenDefined = true;
                }
                else {
                    this._hidden = false;
                    this._isHiddenDefined = false;
                }
                
                if (source.disabled !== undefined) {
                    this._disabled = source.disabled;
                    this._isDisabledDefined = true;
                }
                else {
                    this._disabled = false;
                    this._isDisabledDefined = false;
                }
                
                if (source.assignmentState !== undefined) {
                    this._assignmentState = source.assignmentState;
                    this._isAssignmentStateDefined = true;
                }
                else {
                    this._assignmentState = AssignmentStates.Assignable;
                    this._isAssignmentStateDefined = false;
                }
            }
        }
        else {
            this._label = '';
            this._isLabelDefined = false;
        
            this._value = <any>undefined;
            this._isValueDefined = false;
        
            this._hidden = false;
            this._isHiddenDefined = false;
        
            this._disabled = false;
            this._isDisabledDefined = false;
        
            this._assignmentState = AssignmentStates.Assignable;
            this._isAssignmentStateDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Answer.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isLabelDefined && this.label !== undefined)
            definedProperties.push('label');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isHiddenDefined && this.hidden !== undefined)
            definedProperties.push('hidden');
        if (this.isDisabledDefined && this.disabled !== undefined)
            definedProperties.push('disabled');
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined)
            definedProperties.push('assignmentState');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'label':
                return this.label;
            case 'value':
                return this.value;
            case 'hidden':
                return this.hidden;
            case 'disabled':
                return this.disabled;
            case 'assignmentState':
                return this.assignmentState;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isLabelDefined && this.label !== undefined) {
            if (this.label !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.label);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('label', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'any'));
        
                // Implementation: axon/write/any
                if (_.isString(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('string'));
                    writer.writeStringValue(this.value);    
                }
                else if (_.isInteger(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('int32'));
                    writer.writeIntegerValue(this.value);    
                }
                else if (_.isNumber(this.value)) {
                    writer.writeIndefiniteValueHeader(new Axon.IndefiniteValueHeader('double'));
                    writer.writeDoubleValue(this.value);    
                }
                else {
                    throw new Error('Cannot serialize unknown indefinite type');
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isHiddenDefined && this.hidden !== undefined) {
            if (this.hidden !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hidden', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.hidden);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('hidden', 'null'));
            }
        }
        
        if (this.isDisabledDefined && this.disabled !== undefined) {
            if (this.disabled !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('disabled', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.disabled);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('disabled', 'null'));
            }
        }
        
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            if (this.assignmentState !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.assignmentState);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('assignmentState', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'label':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.label = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'label' cannot be null`);
                    this.label = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'label'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/any
                if (propertyHeader.type === 'any') {
                    const indefiniteValueHeader = reader.readIndefiniteValueHeader();
                
                    switch (indefiniteValueHeader.valueType) {
                        case 'string':
                            this.value = reader.readStringValue();
                            break;
                        case 'int32':
                            this.value = reader.readIntegerValue();
                            break;
                        case 'double':
                            this.value = reader.readDoubleValue();
                            break;
                        default:
                            throw new Error('Cannot serialize unknown indefinite type');
                    }
                }
                else if (propertyHeader.type === 'null') {
                    this.value = null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'hidden':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.hidden = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'hidden' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'hidden'`);
                }
        
                return true;
            case 'disabled':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.disabled = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'disabled' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'disabled'`);
                }
        
                return true;
            case 'assignmentState':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.assignmentState = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'assignmentState' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'assignmentState'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // label
        if (this.isLabelDefined && this.label !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.label === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.label);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/any
            if (this.value === null) {
                writer.writeByteValue(0);
            }
            else if (_.isBoolean(this.value)) {
                writer.writeByteValue(1);
                writer.writeBooleanValue(this.value);
            }
            else if (_.isString(this.value)) {
                writer.writeByteValue(2);
                writer.writeStringValue(this.value);
            }
            else if (_.isInteger(this.value)) {
                writer.writeByteValue(3);
                writer.writeIntegerValue(this.value);
            }
            else if (_.isNumber(this.value)) {
                writer.writeByteValue(5);
                writer.writeDoubleValue(this.value);
            }
            else {
                throw new Error('Unsupported indefinite type');
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // hidden
        if (this.isHiddenDefined && this.hidden !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.hidden);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // disabled
        if (this.isDisabledDefined && this.disabled !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.disabled);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // assignmentState
        if (this.isAssignmentStateDefined && this.assignmentState !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.assignmentState);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // label
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.label = <any>null;
                    break;
                case 1:
                    this.label = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: any
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = null;
                    break;
                case 1:
                    this.value = reader.readBooleanValue();
                    break;
                case 2:
                    this.value = reader.readStringValue();
                    break;
                case 3:
                    this.value = reader.readIntegerValue();
                    break;
                case 4:
                    this.value = reader.readFloatValue();
                    break;
                case 5:
                    this.value = reader.readDoubleValue();
                    break;
                default:
                    throw new Error('Invalid indefinite type code');
            }
        }
        
        // hidden
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.hidden = reader.readBooleanValue();
        }
        
        // disabled
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.disabled = reader.readBooleanValue();
        }
        
        // assignmentState
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.assignmentState = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): Answer {
        // Method Implementation: simple
        const schema = AnswerZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: AnswerZodSchemaShape): Answer {
        // Method Implementation: parseZodSchema
        return new Answer({
            label: schema.label,
            value: schema.value,
            hidden: schema.hidden,
            disabled: schema.disabled,
            assignmentState: (() => {
            if (!schema.assignmentState)
                return;
        
            switch (schema.assignmentState) {
                case 'Assignable':
                    return AssignmentStates.Assignable;
                case 'Conflicting':
                    return AssignmentStates.Conflicting;
                case 'Blocked':
                    return AssignmentStates.Blocked;
                default:
                    throw new Error(`Enum type "schema.assignmentState" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            label: this.isLabelDefined ? ((this.label === null ? undefined : this.label)) : undefined,
            value: this.isValueDefined ? (this.value) : undefined,
            hidden: this.isHiddenDefined ? (this.hidden) : undefined,
            disabled: this.isDisabledDefined ? (this.disabled) : undefined,
            assignmentState: this.isAssignmentStateDefined ? (AssignmentStates[this.assignmentState]) : undefined,
        };
    }

    public clone(): Answer {
        // Method Implementation: clone
        return new Answer({
            label: this.isLabelDefined ? this.label : undefined,
            value: this.isValueDefined ? this.value : undefined,
            hidden: this.isHiddenDefined ? this.hidden : undefined,
            disabled: this.isDisabledDefined ? this.disabled : undefined,
            assignmentState: this.isAssignmentStateDefined ? this.assignmentState : undefined
        });
    }
}

export class KeyedEntity extends ConfigurationProjectionLayout.Entity implements IKeyedEntity {
    public static readonly modelName: string = 'KeyedEntity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'key' ];

    public get modelName(): string {
        // Method Implementation: simple
        return KeyedEntity.modelName;
    }

    private _key: string;

    public get key(): string {
        // Method Implementation: simple
        return this._key;
    }

    public set key(value: string) {
        // Method Implementation: simple
        this._key = value;
        this._isKeyDefined = true;
    }

    private _isKeyDefined: boolean;

    public get isKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isKeyDefined;
    }

    public constructor();
    public constructor(source: IKeyedEntity);
    public constructor(source: Partial<Pick<KeyedEntity, 'key' | 'entityId' | 'groups' | 'validationMessages' | 'conflict' | 'state' | 'historicalStates' | 'layouts'>>);
    public constructor(source?: IKeyedEntity | Partial<Pick<KeyedEntity, 'key' | 'entityId' | 'groups' | 'validationMessages' | 'conflict' | 'state' | 'historicalStates' | 'layouts'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            super(source);
        
            if (isKeyedEntityModel(source)) {
                if (source.isKeyDefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
            }
            else {
                if (source.key !== undefined) {
                    this._key = source.key;
                    this._isKeyDefined = true;
                }
                else {
                    this._key = '';
                    this._isKeyDefined = false;
                }
            }
        }
        else {
            super();
        
            this._key = '';
            this._isKeyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return [ ...super.getPropertyNames(), ...KeyedEntity.propertyNames ];
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [ ...super.getDefinedPropertyNames() ];
        
        if (this.isKeyDefined && this.key !== undefined)
            definedProperties.push('key');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'key':
                return this.key;
            default:
                return super.getPropertyValue(propertyName);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        super.write(writer);
        
        if (this.isKeyDefined && this.key !== undefined) {
            if (this.key !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.key);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('key', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'key':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.key = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'key' cannot be null`);
                    this.key = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'key'`);
                }
        
                return true;
            default:
                return super.readProperty(reader, propertyHeader);
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        super.slipstreamWrite(writer);
        
        // key
        if (this.isKeyDefined && this.key !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.key === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.key);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        super.slipstreamRead(reader);
        
        // key
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.key = <any>null;
                    break;
                case 1:
                    this.key = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): KeyedEntity {
        // Method Implementation: simple
        const schema = KeyedEntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: KeyedEntityZodSchemaShape): KeyedEntity {
        // Method Implementation: parseZodSchema
        return new KeyedEntity({
            entityId: schema.entityId,
            groups: schema.groups?.map(i => ConfigurationProjectionLayout.Group.parseZodSchema(i)),
            validationMessages: schema.validationMessages?.map(i => ConfigurationProjectionLayout.ValidationMessage.parseZodSchema(i)),
            conflict: schema.conflict && ConfigurationProjectionLayout.ConflictScenario.parseZodSchema(schema.conflict),
            state: (() => {
            if (!schema.state)
                return;
        
            switch (schema.state) {
                case 'Valid':
                    return ConfigurationCore.EntityStates.Valid;
                case 'Invalid':
                    return ConfigurationCore.EntityStates.Invalid;
                case 'Incomplete':
                    return ConfigurationCore.EntityStates.Incomplete;
                default:
                    throw new Error(`Enum type "schema.state" not recognized`);
            }
        })(),
            historicalStates: schema.historicalStates?.map(i => ConfigurationProjectionLayout.HistoricalState.parseZodSchema(i)),
            layouts: schema.layouts?.map(i => ConfigurationProjectionLayout.LinkedLayout.parseZodSchema(i)),
            key: schema.key
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityType: 'keyed',
            ...super.toJSON(),
            key: this.isKeyDefined ? ((this.key === null ? undefined : this.key)) : undefined,
        };
    }

    public clone(): KeyedEntity {
        // Method Implementation: clone
        return new KeyedEntity({
            entityId: this.isEntityIdDefined ? this.entityId : undefined,
            groups: this.isGroupsDefined ? this.groups?.map(i => i.clone()) : undefined,
            validationMessages: this.isValidationMessagesDefined ? this.validationMessages?.map(i => i.clone()) : undefined,
            conflict: this.isConflictDefined ? this.conflict.clone() : undefined,
            state: this.isStateDefined ? this.state : undefined,
            historicalStates: this.isHistoricalStatesDefined ? this.historicalStates?.map(i => i.clone()) : undefined,
            layouts: this.isLayoutsDefined ? this.layouts?.map(i => i.clone()) : undefined,
            key: this.isKeyDefined ? this.key : undefined
        });
    }
}

export class CachedEntity implements ICachedEntity {
    public static readonly modelName: string = 'CachedEntity';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'coreEntity', 'layout' ];

    public get modelName(): string {
        // Method Implementation: simple
        return CachedEntity.modelName;
    }

    private _coreEntity: ConfigurationCore.Entity;

    private _layout: ConfigurationModelLayout.Layout;

    public get coreEntity(): ConfigurationCore.Entity {
        // Method Implementation: simple
        return this._coreEntity;
    }

    public get layout(): ConfigurationModelLayout.Layout {
        // Method Implementation: simple
        return this._layout;
    }

    public set coreEntity(value: ConfigurationCore.Entity) {
        // Method Implementation: simple
        this._coreEntity = value;
        this._isCoreEntityDefined = true;
    }

    public set layout(value: ConfigurationModelLayout.Layout) {
        // Method Implementation: simple
        this._layout = value;
        this._isLayoutDefined = true;
    }

    private _isCoreEntityDefined: boolean;

    private _isLayoutDefined: boolean;

    public get isCoreEntityDefined(): boolean {
        // Method Implementation: simple
        return this._isCoreEntityDefined;
    }

    public get isLayoutDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutDefined;
    }

    public constructor();
    public constructor(source: ICachedEntity);
    public constructor(source: Partial<Pick<CachedEntity, 'coreEntity' | 'layout'>>);
    public constructor(source?: ICachedEntity | Partial<Pick<CachedEntity, 'coreEntity' | 'layout'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isCachedEntityModel(source)) {
                if (source.isCoreEntityDefined) {
                    this._coreEntity = source.coreEntity;
                    this._isCoreEntityDefined = true;
                }
                else {
                    this._coreEntity = new ConfigurationCore.Entity();
                    this._isCoreEntityDefined = true;
                }
                
                if (source.isLayoutDefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new ConfigurationModelLayout.Layout();
                    this._isLayoutDefined = true;
                }
            }
            else {
                if (source.coreEntity !== undefined) {
                    this._coreEntity = source.coreEntity;
                    this._isCoreEntityDefined = true;
                }
                else {
                    this._coreEntity = new ConfigurationCore.Entity();
                    this._isCoreEntityDefined = true;
                }
                
                if (source.layout !== undefined) {
                    this._layout = source.layout;
                    this._isLayoutDefined = true;
                }
                else {
                    this._layout = new ConfigurationModelLayout.Layout();
                    this._isLayoutDefined = true;
                }
            }
        }
        else {
            this._coreEntity = new ConfigurationCore.Entity();
            this._isCoreEntityDefined = true;
        
            this._layout = new ConfigurationModelLayout.Layout();
            this._isLayoutDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return CachedEntity.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isCoreEntityDefined && this.coreEntity !== undefined)
            definedProperties.push('coreEntity');
        if (this.isLayoutDefined && this.layout !== undefined)
            definedProperties.push('layout');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'coreEntity':
                return this.coreEntity;
            case 'layout':
                return this.layout;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isCoreEntityDefined && this.coreEntity !== undefined) {
            if (this.coreEntity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('coreEntity', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.coreEntity.getModelName(), this.coreEntity.getDefinedPropertyNames().length));
                this.coreEntity.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('coreEntity', 'null'));
            }
        }
        
        if (this.isLayoutDefined && this.layout !== undefined) {
            if (this.layout !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'model'));
        
                // Implementation: axon/write/model
                writer.writeModelHeader(new Axon.ModelHeader(this.layout.getModelName(), this.layout.getDefinedPropertyNames().length));
                this.layout.write(writer);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layout', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'coreEntity':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConfigurationCore.Entity.modelName) {
                        this.coreEntity = new ConfigurationCore.Entity();
                        this.coreEntity.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConfigurationCore.Entity.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'coreEntity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'coreEntity'`);
                }
        
                return true;
            case 'layout':
                // Implementation: axon/readProperty/model
                if (propertyHeader.type === 'model') {
                    const modelHeader = reader.readModelHeader();
                
                    if (modelHeader.modelName === ConfigurationModelLayout.Layout.modelName) {
                        this.layout = new ConfigurationModelLayout.Layout();
                        this.layout.read(reader, modelHeader);
                    }
                    else {
                        throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ConfigurationModelLayout.Layout.modelName}'`);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'layout' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layout'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // coreEntity
        if (this.isCoreEntityDefined && this.coreEntity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.coreEntity === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.coreEntity.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layout
        if (this.isLayoutDefined && this.layout !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/model
            if (this.layout === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                this.layout.slipstreamWrite(writer);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // coreEntity
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.coreEntity = new ConfigurationCore.Entity();
                    this.coreEntity.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layout
        if (reader.readBooleanValue())
        {
            // Implementation: model
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null models');
                    break;
                case 1:
                    this.layout = new ConfigurationModelLayout.Layout();
                    this.layout.slipstreamRead(reader);
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): CachedEntity {
        // Method Implementation: simple
        const schema = CachedEntityZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: CachedEntityZodSchemaShape): CachedEntity {
        // Method Implementation: parseZodSchema
        return new CachedEntity({
            coreEntity: schema.coreEntity && ConfigurationCore.Entity.parseZodSchema(schema.coreEntity),
            layout: schema.layout && ConfigurationModelLayout.Layout.parseZodSchema(schema.layout)
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            coreEntity: this.isCoreEntityDefined ? (this.coreEntity) : undefined,
            layout: this.isLayoutDefined ? (this.layout) : undefined,
        };
    }

    public clone(): CachedEntity {
        // Method Implementation: clone
        return new CachedEntity({
            coreEntity: this.isCoreEntityDefined ? this.coreEntity.clone() : undefined,
            layout: this.isLayoutDefined ? this.layout.clone() : undefined
        });
    }
}

export class EntityState implements IEntityState {
    public static readonly modelName: string = 'EntityState';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityName', 'questions' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityState.modelName;
    }

    private _entityName: string;

    private _questions: EntityStateQuestion[];

    public get entityName(): string {
        // Method Implementation: simple
        return this._entityName;
    }

    public get questions(): EntityStateQuestion[] {
        // Method Implementation: simple
        return this._questions;
    }

    public set entityName(value: string) {
        // Method Implementation: simple
        this._entityName = value;
        this._isEntityNameDefined = true;
    }

    public set questions(value: EntityStateQuestion[]) {
        // Method Implementation: simple
        this._questions = value;
        this._isQuestionsDefined = true;
    }

    private _isEntityNameDefined: boolean;

    private _isQuestionsDefined: boolean;

    public get isEntityNameDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityNameDefined;
    }

    public get isQuestionsDefined(): boolean {
        // Method Implementation: simple
        return this._isQuestionsDefined;
    }

    public constructor();
    public constructor(source: IEntityState);
    public constructor(source: Partial<Pick<EntityState, 'entityName' | 'questions'>>);
    public constructor(source?: IEntityState | Partial<Pick<EntityState, 'entityName' | 'questions'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityStateModel(source)) {
                if (source.isEntityNameDefined) {
                    this._entityName = source.entityName;
                    this._isEntityNameDefined = true;
                }
                else {
                    this._entityName = '';
                    this._isEntityNameDefined = false;
                }
                
                if (source.isQuestionsDefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
            else {
                if (source.entityName !== undefined) {
                    this._entityName = source.entityName;
                    this._isEntityNameDefined = true;
                }
                else {
                    this._entityName = '';
                    this._isEntityNameDefined = false;
                }
                
                if (source.questions !== undefined) {
                    this._questions = source.questions;
                    this._isQuestionsDefined = true;
                }
                else {
                    this._questions = [];
                    this._isQuestionsDefined = true;
                }
            }
        }
        else {
            this._entityName = '';
            this._isEntityNameDefined = false;
        
            this._questions = [];
            this._isQuestionsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityState.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityNameDefined && this.entityName !== undefined)
            definedProperties.push('entityName');
        if (this.isQuestionsDefined && this.questions !== undefined)
            definedProperties.push('questions');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityName':
                return this.entityName;
            case 'questions':
                return this.questions;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityNameDefined && this.entityName !== undefined) {
            if (this.entityName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityName', 'null'));
            }
        }
        
        if (this.isQuestionsDefined && this.questions !== undefined) {
            if (this.questions !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.questions.length));
                for (let i = 0; i < this.questions.length; i++) {
                    const item = this.questions[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('questions', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityName' cannot be null`);
                    this.entityName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityName'`);
                }
        
                return true;
            case 'questions':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.questions = [];
                
                    let item: EntityStateQuestion = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === EntityStateQuestion.modelName) {
                                item = new EntityStateQuestion();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${EntityStateQuestion.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'questions' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'questions'`);
                        }
                
                        this.questions.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'questions' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'questions'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityName
        if (this.isEntityNameDefined && this.entityName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // questions
        if (this.isQuestionsDefined && this.questions !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.questions === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.questions.length);
            
                for (const item of this.questions) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityName = <any>null;
                    break;
                case 1:
                    this.entityName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // questions
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: EntityStateQuestion;
            
                    this.questions = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new EntityStateQuestion();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.questions.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): EntityState {
        // Method Implementation: simple
        const schema = EntityStateZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityStateZodSchemaShape): EntityState {
        // Method Implementation: parseZodSchema
        return new EntityState({
            entityName: schema.entityName,
            questions: schema.questions?.map(i => EntityStateQuestion.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityName: this.isEntityNameDefined ? ((this.entityName === null ? undefined : this.entityName)) : undefined,
            questions: this.isQuestionsDefined ? (this.questions?.map(item => item)) : undefined,
        };
    }

    public clone(): EntityState {
        // Method Implementation: clone
        return new EntityState({
            entityName: this.isEntityNameDefined ? this.entityName : undefined,
            questions: this.isQuestionsDefined ? this.questions?.map(i => i.clone()) : undefined
        });
    }
}

export class EntityStateQuestion implements IEntityStateQuestion {
    public static readonly modelName: string = 'EntityStateQuestion';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'value', 'restrict' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityStateQuestion.modelName;
    }

    private _name: string;

    private _value: string;

    private _restrict?: boolean;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get value(): string {
        // Method Implementation: simple
        return this._value;
    }

    public get restrict(): boolean | undefined {
        // Method Implementation: simple
        return this._restrict;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set value(value: string) {
        // Method Implementation: simple
        this._value = value;
        this._isValueDefined = true;
    }

    public set restrict(value: boolean | undefined) {
        // Method Implementation: simple
        this._restrict = value;
        this._isRestrictDefined = true;
    }

    private _isNameDefined: boolean;

    private _isValueDefined: boolean;

    private _isRestrictDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isValueDefined(): boolean {
        // Method Implementation: simple
        return this._isValueDefined;
    }

    public get isRestrictDefined(): boolean {
        // Method Implementation: simple
        return this._isRestrictDefined;
    }

    public constructor();
    public constructor(source: IEntityStateQuestion);
    public constructor(source: Partial<Pick<EntityStateQuestion, 'name' | 'value' | 'restrict'>>);
    public constructor(source?: IEntityStateQuestion | Partial<Pick<EntityStateQuestion, 'name' | 'value' | 'restrict'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityStateQuestionModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isValueDefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.isRestrictDefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.value !== undefined) {
                    this._value = source.value;
                    this._isValueDefined = true;
                }
                else {
                    this._value = '';
                    this._isValueDefined = false;
                }
                
                if (source.restrict !== undefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._value = '';
            this._isValueDefined = false;
        
            this._restrict = false;
            this._isRestrictDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityStateQuestion.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isValueDefined && this.value !== undefined)
            definedProperties.push('value');
        if (this.isRestrictDefined && this.restrict !== undefined)
            definedProperties.push('restrict');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'value':
                return this.value;
            case 'restrict':
                return this.restrict;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isValueDefined && this.value !== undefined) {
            if (this.value !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.value);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('value', 'null'));
            }
        }
        
        if (this.isRestrictDefined && this.restrict !== undefined) {
            if (this.restrict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.restrict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'value':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.value = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'value' cannot be null`);
                    this.value = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'value'`);
                }
        
                return true;
            case 'restrict':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.restrict = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'restrict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'restrict'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // value
        if (this.isValueDefined && this.value !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.value === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.value);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // restrict
        if (this.isRestrictDefined && this.restrict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.restrict);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // value
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.value = <any>null;
                    break;
                case 1:
                    this.value = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // restrict
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.restrict = reader.readBooleanValue();
        }
    }

    public static parse(hash: unknown): EntityStateQuestion {
        // Method Implementation: simple
        const schema = EntityStateQuestionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityStateQuestionZodSchemaShape): EntityStateQuestion {
        // Method Implementation: parseZodSchema
        return new EntityStateQuestion({
            name: schema.name,
            value: schema.value,
            restrict: schema.restrict
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            value: this.isValueDefined ? ((this.value === null ? undefined : this.value)) : undefined,
            restrict: this.isRestrictDefined ? (this.restrict) : undefined,
        };
    }

    public clone(): EntityStateQuestion {
        // Method Implementation: clone
        return new EntityStateQuestion({
            name: this.isNameDefined ? this.name : undefined,
            value: this.isValueDefined ? this.value : undefined,
            restrict: this.isRestrictDefined ? this.restrict : undefined
        });
    }
}

export class EntityContext implements IEntityContext {
    public static readonly modelName: string = 'EntityContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey', 'layoutName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return EntityContext.modelName;
    }

    private _entityKey: string;

    private _layoutName: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get layoutName(): string {
        // Method Implementation: simple
        return this._layoutName;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set layoutName(value: string) {
        // Method Implementation: simple
        this._layoutName = value;
        this._isLayoutNameDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    private _isLayoutNameDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isLayoutNameDefined(): boolean {
        // Method Implementation: simple
        return this._isLayoutNameDefined;
    }

    public constructor();
    public constructor(source: IEntityContext);
    public constructor(source: Partial<Pick<EntityContext, 'entityKey' | 'layoutName'>>);
    public constructor(source?: IEntityContext | Partial<Pick<EntityContext, 'entityKey' | 'layoutName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isEntityContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isLayoutNameDefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.layoutName !== undefined) {
                    this._layoutName = source.layoutName;
                    this._isLayoutNameDefined = true;
                }
                else {
                    this._layoutName = '';
                    this._isLayoutNameDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._layoutName = '';
            this._isLayoutNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return EntityContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isLayoutNameDefined && this.layoutName !== undefined)
            definedProperties.push('layoutName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            case 'layoutName':
                return this.layoutName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            if (this.layoutName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.layoutName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('layoutName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'layoutName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.layoutName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'layoutName' cannot be null`);
                    this.layoutName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'layoutName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // layoutName
        if (this.isLayoutNameDefined && this.layoutName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.layoutName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.layoutName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // layoutName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.layoutName = <any>null;
                    break;
                case 1:
                    this.layoutName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): EntityContext {
        // Method Implementation: simple
        const schema = EntityContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: EntityContextZodSchemaShape): EntityContext {
        // Method Implementation: parseZodSchema
        return new EntityContext({
            entityKey: schema.entityKey,
            layoutName: schema.layoutName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            layoutName: this.isLayoutNameDefined ? ((this.layoutName === null ? undefined : this.layoutName)) : undefined,
        };
    }

    public clone(): EntityContext {
        // Method Implementation: clone
        return new EntityContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            layoutName: this.isLayoutNameDefined ? this.layoutName : undefined
        });
    }
}

export class GridContext implements IGridContext {
    public static readonly modelName: string = 'GridContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey', 'gridName' ];

    public get modelName(): string {
        // Method Implementation: simple
        return GridContext.modelName;
    }

    private _entityKey: string;

    private _gridName: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public get gridName(): string {
        // Method Implementation: simple
        return this._gridName;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    public set gridName(value: string) {
        // Method Implementation: simple
        this._gridName = value;
        this._isGridNameDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    private _isGridNameDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public get isGridNameDefined(): boolean {
        // Method Implementation: simple
        return this._isGridNameDefined;
    }

    public constructor();
    public constructor(source: IGridContext);
    public constructor(source: Partial<Pick<GridContext, 'entityKey' | 'gridName'>>);
    public constructor(source?: IGridContext | Partial<Pick<GridContext, 'entityKey' | 'gridName'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isGridContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.isGridNameDefined) {
                    this._gridName = source.gridName;
                    this._isGridNameDefined = true;
                }
                else {
                    this._gridName = '';
                    this._isGridNameDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
                
                if (source.gridName !== undefined) {
                    this._gridName = source.gridName;
                    this._isGridNameDefined = true;
                }
                else {
                    this._gridName = '';
                    this._isGridNameDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        
            this._gridName = '';
            this._isGridNameDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return GridContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        if (this.isGridNameDefined && this.gridName !== undefined)
            definedProperties.push('gridName');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            case 'gridName':
                return this.gridName;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
        
        if (this.isGridNameDefined && this.gridName !== undefined) {
            if (this.gridName !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('gridName', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.gridName);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('gridName', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            case 'gridName':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.gridName = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'gridName' cannot be null`);
                    this.gridName = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'gridName'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // gridName
        if (this.isGridNameDefined && this.gridName !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.gridName === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.gridName);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // gridName
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.gridName = <any>null;
                    break;
                case 1:
                    this.gridName = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): GridContext {
        // Method Implementation: simple
        const schema = GridContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: GridContextZodSchemaShape): GridContext {
        // Method Implementation: parseZodSchema
        return new GridContext({
            entityKey: schema.entityKey,
            gridName: schema.gridName
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
            gridName: this.isGridNameDefined ? ((this.gridName === null ? undefined : this.gridName)) : undefined,
        };
    }

    public clone(): GridContext {
        // Method Implementation: clone
        return new GridContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined,
            gridName: this.isGridNameDefined ? this.gridName : undefined
        });
    }
}

export class StateContext implements IStateContext {
    public static readonly modelName: string = 'StateContext';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'entityKey' ];

    public get modelName(): string {
        // Method Implementation: simple
        return StateContext.modelName;
    }

    private _entityKey: string;

    public get entityKey(): string {
        // Method Implementation: simple
        return this._entityKey;
    }

    public set entityKey(value: string) {
        // Method Implementation: simple
        this._entityKey = value;
        this._isEntityKeyDefined = true;
    }

    private _isEntityKeyDefined: boolean;

    public get isEntityKeyDefined(): boolean {
        // Method Implementation: simple
        return this._isEntityKeyDefined;
    }

    public constructor();
    public constructor(source: IStateContext);
    public constructor(source: Partial<Pick<StateContext, 'entityKey'>>);
    public constructor(source?: IStateContext | Partial<Pick<StateContext, 'entityKey'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isStateContextModel(source)) {
                if (source.isEntityKeyDefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
            }
            else {
                if (source.entityKey !== undefined) {
                    this._entityKey = source.entityKey;
                    this._isEntityKeyDefined = true;
                }
                else {
                    this._entityKey = '';
                    this._isEntityKeyDefined = false;
                }
            }
        }
        else {
            this._entityKey = '';
            this._isEntityKeyDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return StateContext.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isEntityKeyDefined && this.entityKey !== undefined)
            definedProperties.push('entityKey');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'entityKey':
                return this.entityKey;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            if (this.entityKey !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.entityKey);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('entityKey', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'entityKey':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.entityKey = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'entityKey' cannot be null`);
                    this.entityKey = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'entityKey'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // entityKey
        if (this.isEntityKeyDefined && this.entityKey !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.entityKey === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.entityKey);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // entityKey
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.entityKey = <any>null;
                    break;
                case 1:
                    this.entityKey = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): StateContext {
        // Method Implementation: simple
        const schema = StateContextZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: StateContextZodSchemaShape): StateContext {
        // Method Implementation: parseZodSchema
        return new StateContext({
            entityKey: schema.entityKey
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            entityKey: this.isEntityKeyDefined ? ((this.entityKey === null ? undefined : this.entityKey)) : undefined,
        };
    }

    public clone(): StateContext {
        // Method Implementation: clone
        return new StateContext({
            entityKey: this.isEntityKeyDefined ? this.entityKey : undefined
        });
    }
}

export interface IEntityService {
    init(context: EntityContext, entityKey: string, layoutName: string): Promise<void>;

    get(context: EntityContext): Promise<ConfigurationProjectionLayout.Entity>;

    getLayout(context: EntityContext): Promise<Layout>;

    update(context: EntityContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayout.Entity>;

    updateLayout(context: EntityContext, updates: EntityStateQuestion[]): Promise<Layout>;

    restore(context: EntityContext, deltaIndex: number): Promise<ConfigurationProjectionLayout.Entity>;
}

export interface IGridService {
    init(context: GridContext, entityKey: string, gridName: string): Promise<void>;

    get(context: GridContext): Promise<ConfigurationProjectionLayoutGrid.Grid>;

    update(context: GridContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayoutGrid.Grid>;
}

export interface IStateService {
    init(context: StateContext, entityKey: string): Promise<void>;

    get(context: StateContext): Promise<ConfigurationProjectionIntegration.Entity>;
}

export class EntityServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: EntityContext, entityKey: string, layoutName: string): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: EntityContext, entityKey: string, layoutName: string): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, entityKey, layoutName ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("fddc5d2b221085582f1f4131f6ca90e8bcc8416072002895688cc21180338a99");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (entityKey && entityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (entityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(entityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (layoutName && layoutName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (layoutName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(layoutName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                entityKey: JSON.parse(JSON.stringify(entityKey)),
                                layoutName: JSON.parse(JSON.stringify(layoutName))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        layoutName: JSON.parse(JSON.stringify(layoutName))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        layoutName: JSON.parse(JSON.stringify(layoutName))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public get(context: EntityContext): Promise<ConfigurationProjectionLayout.Entity>;
    public get(actionContext: Wellspring.ActionContext, context: EntityContext): Promise<ConfigurationProjectionLayout.Entity>;
    public async get(...args: unknown[]): Promise<ConfigurationProjectionLayout.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.get');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('get', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("f01e6039fb55dfedf480a4d15c6ff1ad81f3cb03b97a8106c72d3b82e4627a3d");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].get`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/get" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public getLayout(context: EntityContext): Promise<Layout>;
    public getLayout(actionContext: Wellspring.ActionContext, context: EntityContext): Promise<Layout>;
    public async getLayout(...args: unknown[]): Promise<Layout> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.getLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('getLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("2b754a0261211f76f5494cc8e0791ab5b37d3fd081637d13901b71fc3bf72f7f");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Layout>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].getLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/getLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: Layout;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Layout();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].getLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].getLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(context: EntityContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayout.Entity>;
    public update(actionContext: Wellspring.ActionContext, context: EntityContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayout.Entity>;
    public async update(...args: unknown[]): Promise<ConfigurationProjectionLayout.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext), 
            // {"template":"array","itemImplementation":{"template":"model","className":"EntityStateQuestion"}}
            Zod.array(Zod.instanceof(EntityStateQuestion))
        ]).check(args)) {
            const [ context, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("02549875dc3ddf3ce0093d7d084f23c15ce8159f120bf7f30fcca8fac6c6f466");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public updateLayout(context: EntityContext, updates: EntityStateQuestion[]): Promise<Layout>;
    public updateLayout(actionContext: Wellspring.ActionContext, context: EntityContext, updates: EntityStateQuestion[]): Promise<Layout>;
    public async updateLayout(...args: unknown[]): Promise<Layout> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext), 
            // {"template":"array","itemImplementation":{"template":"model","className":"EntityStateQuestion"}}
            Zod.array(Zod.instanceof(EntityStateQuestion))
        ]).check(args)) {
            const [ context, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.updateLayout');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('updateLayout', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(4);
                    writer.writeStringValue("2222e1a0d79fc08265c6f1888814bf665cf15996483bab321e161fe67aec96c6");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Layout>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].updateLayout`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/updateLayout" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: Layout;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Layout();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].updateLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].updateLayout`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public restore(context: EntityContext, deltaIndex: number): Promise<ConfigurationProjectionLayout.Entity>;
    public restore(actionContext: Wellspring.ActionContext, context: EntityContext, deltaIndex: number): Promise<ConfigurationProjectionLayout.Entity>;
    public async restore(...args: unknown[]): Promise<ConfigurationProjectionLayout.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"EntityContext"}
            Zod.instanceof(EntityContext), 
            // {"template":"number"}
            Zod.number()
        ]).check(args)) {
            const [ context, deltaIndex ] = args;
        
            const activity = new Wellspring.DistributedActivity('entity.restore');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('restore', 'utf8'));
                requestMetadata.add('service', Buffer.from('entity', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(5);
                    writer.writeStringValue("cd6755d5c2159e3679b7daafcebcd9dadeb403fdd57276d1b4a79add49dc2699");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (deltaIndex && deltaIndex !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/int32
                        writer.writeIntegerValue(deltaIndex);
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionLayout.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].restore`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "entity/restore" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionLayout.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionLayout.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `entity[${this.identifier}].restore`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `entity[${this.identifier}].restore`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        deltaIndex: JSON.parse(JSON.stringify(deltaIndex))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class GridServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: GridContext, entityKey: string, gridName: string): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: GridContext, entityKey: string, gridName: string): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"GridContext"}
            Zod.instanceof(GridContext), 
            // {"template":"string"}
            Zod.string(), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, entityKey, gridName ] = args;
        
            const activity = new Wellspring.DistributedActivity('grid.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('grid', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("7a3a59f14f097c7bdbf386f4c80b850995edc2767ea8e9b079362394879daae5");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (entityKey && entityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (entityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(entityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (gridName && gridName !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (gridName === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(gridName);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                entityKey: JSON.parse(JSON.stringify(entityKey)),
                                gridName: JSON.parse(JSON.stringify(gridName))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "grid/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `grid[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        gridName: JSON.parse(JSON.stringify(gridName))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey)),
                        gridName: JSON.parse(JSON.stringify(gridName))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public get(context: GridContext): Promise<ConfigurationProjectionLayoutGrid.Grid>;
    public get(actionContext: Wellspring.ActionContext, context: GridContext): Promise<ConfigurationProjectionLayoutGrid.Grid>;
    public async get(...args: unknown[]): Promise<ConfigurationProjectionLayoutGrid.Grid> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"GridContext"}
            Zod.instanceof(GridContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('grid.get');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('get', 'utf8'));
                requestMetadata.add('service', Buffer.from('grid', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("add431d691b6ff06ecc727c144e9192db0c0a8b677d5c73ce38d9ce26a507e3d");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionLayoutGrid.Grid>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].get`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "grid/get" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionLayoutGrid.Grid;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionLayoutGrid.Grid();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `grid[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public update(context: GridContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayoutGrid.Grid>;
    public update(actionContext: Wellspring.ActionContext, context: GridContext, updates: EntityStateQuestion[]): Promise<ConfigurationProjectionLayoutGrid.Grid>;
    public async update(...args: unknown[]): Promise<ConfigurationProjectionLayoutGrid.Grid> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"GridContext"}
            Zod.instanceof(GridContext), 
            // {"template":"array","itemImplementation":{"template":"model","className":"EntityStateQuestion"}}
            Zod.array(Zod.instanceof(EntityStateQuestion))
        ]).check(args)) {
            const [ context, updates ] = args;
        
            const activity = new Wellspring.DistributedActivity('grid.update');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('update', 'utf8'));
                requestMetadata.add('service', Buffer.from('grid', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("7617edf8170e58f4dd2615a3a5d3929f50b664cde3ec68c327ec9707c9572c2a");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (updates && updates !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/array
                        if (updates === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            writer.writeIntegerValue(updates.length);
                        
                            for (const item of updates) {
                                // Item Implementation: model
                                if (item === null) {
                                    writer.writeByteValue(0);
                                }
                                else {
                                    writer.writeByteValue(1);
                                    item.slipstreamWrite(writer);
                                }
                            }
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionLayoutGrid.Grid>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].update`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                updates: JSON.parse(JSON.stringify(updates))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "grid/update" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionLayoutGrid.Grid;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionLayoutGrid.Grid();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `grid[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `grid[${this.identifier}].update`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        updates: JSON.parse(JSON.stringify(updates))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class StateServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public init(context: StateContext, entityKey: string): Promise<void>;
    public init(actionContext: Wellspring.ActionContext, context: StateContext, entityKey: string): Promise<void>;
    public async init(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"StateContext"}
            Zod.instanceof(StateContext), 
            // {"template":"string"}
            Zod.string()
        ]).check(args)) {
            const [ context, entityKey ] = args;
        
            const activity = new Wellspring.DistributedActivity('state.init');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('init', 'utf8'));
                requestMetadata.add('service', Buffer.from('state', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("d692349d5190293cb86ede1711cbea54f82f0b2a0096a50a11b57daff96f5036");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (entityKey && entityKey !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/string
                        if (entityKey === null)
                        {
                            writer.writeByteValue(0);
                        }
                        else
                        {
                            writer.writeByteValue(1);
                            writer.writeStringValue(entityKey);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `state[${this.identifier}].init`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context)),
                                entityKey: JSON.parse(JSON.stringify(entityKey))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "state/init" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `state[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `state[${this.identifier}].init`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context)),
                        entityKey: JSON.parse(JSON.stringify(entityKey))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public get(context: StateContext): Promise<ConfigurationProjectionIntegration.Entity>;
    public get(actionContext: Wellspring.ActionContext, context: StateContext): Promise<ConfigurationProjectionIntegration.Entity>;
    public async get(...args: unknown[]): Promise<ConfigurationProjectionIntegration.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"StateContext"}
            Zod.instanceof(StateContext)
        ]).check(args)) {
            const [ context ] = args;
        
            const activity = new Wellspring.DistributedActivity('state.get');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('get', 'utf8'));
                requestMetadata.add('service', Buffer.from('state', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("3bfa707c7b494a701c00d2e2b71a7a73bf77fd26a5e311ab0163e39cafd06f5f");
        
                    if (context && context !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (context === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            context.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationProjectionIntegration.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `state[${this.identifier}].get`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                context: JSON.parse(JSON.stringify(context))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "state/get" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            context.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    let result: ConfigurationProjectionIntegration.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationProjectionIntegration.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `state[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `state[${this.identifier}].get`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        context: JSON.parse(JSON.stringify(context))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Layout':
                return new Layout();
            case 'Group':
                return new Group();
            case 'Question':
                return new Question();
            case 'Answer':
                return new Answer();
            case 'KeyedEntity':
                return new KeyedEntity();
            case 'CachedEntity':
                return new CachedEntity();
            case 'EntityState':
                return new EntityState();
            case 'EntityStateQuestion':
                return new EntityStateQuestion();
            case 'EntityContext':
                return new EntityContext();
            case 'GridContext':
                return new GridContext();
            case 'StateContext':
                return new StateContext();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Layout':
                return new Layout(source);
            case 'Group':
                return new Group(source);
            case 'Question':
                return new Question(source);
            case 'Answer':
                return new Answer(source);
            case 'KeyedEntity':
                return new KeyedEntity(source);
            case 'CachedEntity':
                return new CachedEntity(source);
            case 'EntityState':
                return new EntityState(source);
            case 'EntityStateQuestion':
                return new EntityStateQuestion(source);
            case 'EntityContext':
                return new EntityContext(source);
            case 'GridContext':
                return new GridContext(source);
            case 'StateContext':
                return new StateContext(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Layout':
                return Layout.parse(source);
            case 'Group':
                return Group.parse(source);
            case 'Question':
                return Question.parse(source);
            case 'Answer':
                return Answer.parse(source);
            case 'KeyedEntity':
                return KeyedEntity.parse(source);
            case 'CachedEntity':
                return CachedEntity.parse(source);
            case 'EntityState':
                return EntityState.parse(source);
            case 'EntityStateQuestion':
                return EntityStateQuestion.parse(source);
            case 'EntityContext':
                return EntityContext.parse(source);
            case 'GridContext':
                return GridContext.parse(source);
            case 'StateContext':
                return StateContext.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
